// `App.PlaceIdentity` never carries a `name`, so `'name' in place` alone is enough to
// narrow to a resolved `App.Place`.
export function isResolvedPlace(place: App.Place | App.PlaceIdentity | undefined): place is App.Place {
  return !!place && 'name' in place
}

export function getPlaceCachePartition(
  placeCacheByLocale: App.DestinationState['placeCacheByLocale'],
  locale: string,
): Record<string, App.PlaceCacheEntry> {
  return placeCacheByLocale[locale] ?? {}
}

export function getReadyPlaceFromCacheEntry(entry?: App.PlaceCacheEntry): App.Place | undefined {
  return entry?.status === 'ready' ? entry.place : undefined
}

export function withIdentityFallback(place: App.Place, identity?: App.PlaceIdentity): App.Place {
  if (!identity) return place
  if (place.countryCode && place.type) return place
  return {
    ...place,
    countryCode: place.countryCode ?? identity.countryCode,
    type: place.type ?? identity.type,
  }
}

export function setPlaceCacheEntries(
  placeCacheByLocale: App.DestinationState['placeCacheByLocale'],
  locale: string,
  updates: Record<string, App.PlaceCacheEntry>,
): App.DestinationState['placeCacheByLocale'] {
  return {
    ...placeCacheByLocale,
    [locale]: {
      ...getPlaceCachePartition(placeCacheByLocale, locale),
      ...updates,
    },
  }
}

export function mergeReadyPlacesIntoLegacyMap(
  places: App.DestinationState['places'],
  locale: string,
  placeCacheByLocale: App.DestinationState['placeCacheByLocale'],
  placeIds: ReadonlyArray<string>,
): App.DestinationState['places'] {
  const partition = getPlaceCachePartition(placeCacheByLocale, locale)
  const nextPlaces = { ...places }
  for (const id of placeIds) {
    const readyPlace = getReadyPlaceFromCacheEntry(partition[id])
    if (readyPlace) {
      nextPlaces[id] = readyPlace
    }
  }
  return nextPlaces
}
